import {
  Dialog,
  IconButton,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
} from "@material-ui/core"
import React, { FC, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import {
  useDeletePractitionerByEmailMutation,
  useDeleteUserInviteByEmailMutation,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { Query, Role } from "../../utils/constants"
import { colors, theme } from "../../utils/theme"
import { GhostButton, PrimaryButton } from "../atoms/button/Button"
import CloseIcon from "../atoms/CloseIcon"
import MoreIcon from "../atoms/MoreIcon"
import AddStaffDialog from "./AddStaffDialog"

const useStyles = makeStyles({
  moreButton: {
    fill: colors.neutral.denim,
    stroke: colors.neutral.denim,
    "&:hover": {
      fill: colors.neutral.asphalt,
      stroke: colors.neutral.asphalt,
    },
    "&:active": {
      fill: colors.primary.periwinkle,
      stroke: colors.primary.periwinkle,
    },
    "&:focus": {
      fill: colors.primary.shadow,
      stroke: colors.primary.shadow,
    },
  },
  dialogPaper: {
    border: `1px solid ${colors.neutral.clay}`,
    boxShadow: "none",
  },
  dialogCloseContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(2),
    paddingBottom: theme.spacing(5),
  },
  closeButton: {
    margin: 0,
    padding: 0,
    border: 0,
    height: theme.spacing(2),
    width: theme.spacing(2),
    backgroundColor: "transparent",
    cursor: "pointer",
    "&:focus": {
      outline: "none",
    },
  },
  dialogTitle: {
    color: colors.neutral.denim,
    marginBottom: 12,
  },
  dialogSubtitle: {
    color: colors.neutral.denim,
    marginBottom: theme.spacing(3),
  },
  confirmButton: {
    marginBottom: theme.spacing(2),
    width: "100%",
  },
  cancelContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  menuPaper: {
    borderRadius: 4,
    border: "1px solid rgba(0, 0, 0, 0.1)",
    backgroundColor: colors.neutral.white,
    boxShadow:
      "0px 0px 4px rgba(54, 53, 69, 0.1), 0px 3px 12px rgba(54, 53, 69, 0.05)",
  },
  iconButton: {
    "&:hover": {
      backgroundColor: "transparent",
      boxShadow: "none",
      outline: "none",
    },
    "&:focus": {
      outline: "none",
    },
  },
})

const StaffMoreButton: FC<{
  staffId: string | number
  firstName: string
  lastName: string
  email: string
  role: Role
  isPending: boolean
}> = ({ staffId, firstName, lastName, email, role, isPending }) => {
  const classes = useStyles()
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false)
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const graphqlClient = useGraphQLClient()
  const queryClient = useQueryClient()
  const onSuccess = () => {
    queryClient.invalidateQueries({ queryKey: [Query.GET_ORGANIZATION] })
    queryClient.invalidateQueries({
      queryKey: [Query.GET_ORG_PRACTITIONER_COUNTS],
    })
    queryClient.invalidateQueries({
      queryKey: [Query.GET_SUPERVISORS_BY_ORG_ID],
    })
  }
  const { mutate: deletePractitioner } = useDeletePractitionerByEmailMutation(
    graphqlClient,
    { onSuccess }
  )
  const { mutate: deleteUserInvite } = useDeleteUserInviteByEmailMutation(
    graphqlClient,
    { onSuccess }
  )

  return (
    <>
      <IconButton
        onClick={(event) => setAnchorEl(event.currentTarget)}
        className={classes.iconButton}
        classes={{ label: classes.moreButton }}
      >
        <MoreIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        keepMounted={true}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        classes={{
          paper: classes.menuPaper,
        }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            setOpenEditDialog(true)
          }}
        >
          Edit Staff Member
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null)
            setOpenDeleteDialog(true)
          }}
        >
          Remove Staff Member
        </MenuItem>
      </Menu>

      <Dialog
        fullWidth={true}
        maxWidth="xs"
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        classes={{
          paper: classes.dialogPaper,
        }}
      >
        <div className={classes.dialogContent}>
          <div className={classes.dialogCloseContainer}>
            <button
              className={classes.closeButton}
              onClick={() => setOpenDeleteDialog(false)}
            >
              <CloseIcon />
            </button>
          </div>
          <Typography
            variant="h2"
            component="h1"
            className={classes.dialogTitle}
          >
            Remove Client
          </Typography>
          <Typography variant="body1" className={classes.dialogSubtitle}>
            {`Are you sure you want to remove ${firstName} ${lastName}? ` +
              "Their Vira app data will no longer be accessible after removal."}
          </Typography>
          <PrimaryButton
            onClick={() => {
              deleteUserInvite({
                email,
              })
              if (!isPending) {
                deletePractitioner({
                  email,
                })
              }
              setOpenDeleteDialog(false)
            }}
            className={classes.confirmButton}
            type="submit"
          >
            Confirm
          </PrimaryButton>
          <div className={classes.cancelContainer}>
            <GhostButton onClick={async () => setOpenDeleteDialog(false)}>
              Cancel
            </GhostButton>
          </div>
        </div>
      </Dialog>
      {openEditDialog && (
        <AddStaffDialog
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          isEdit={true}
          isPending={isPending}
          id={staffId}
          currentFirstName={firstName}
          currentLastName={lastName}
          currentEmail={email}
          currentRole={role}
        />
      )}
    </>
  )
}

export default StaffMoreButton
